import { useCallback, useState } from "react";
import { SmsTemplate } from "./types";
import { loadTemplates, saveTemplates } from "./templatesStorage";
import { syncIfAutomationEnabled } from "../settings/settingsSyncApi";

export function useTemplates() {
  const [templates, setTemplates] = useState<SmsTemplate[]>(() => loadTemplates());
  const [saving, setSaving] = useState(false);
  /** Set when the backend copy couldn't be updated; the local save still stands. */
  const [syncError, setSyncError] = useState<string | undefined>();

  const persist = useCallback(async (next: SmsTemplate[]) => {
    setTemplates(next);
    setSaving(true);
    try {
      await saveTemplates(next);
      // Calendar automation sends from the server's copy of the templates, so a
      // local-only change would silently keep sending the old text.
      setSyncError(await syncIfAutomationEnabled());
    } finally {
      setSaving(false);
    }
  }, []);

  const upsert = useCallback(
    async (template: SmsTemplate) => {
      const exists = templates.some((t) => t.id === template.id);
      let next = exists
        ? templates.map((t) => (t.id === template.id ? template : t))
        : [...templates, template];
      if (template.isDefault) {
        next = next.map((t) => (t.id === template.id ? t : { ...t, isDefault: false }));
      }
      await persist(next);
    },
    [templates, persist]
  );

  const remove = useCallback(
    async (id: string) => {
      await persist(templates.filter((t) => t.id !== id));
    },
    [templates, persist]
  );

  return { templates, upsert, remove, saving, syncError };
}
